import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { MoreIcon, PencilIcon, TrashIcon } from './icons';
import { CoworkerGroupMenu, CoworkerGroupMenuDivider, CoworkerGroupMenuItem, CoworkerGroupMenuTrigger } from './styles';

interface CoworkerGroupMenuProps {
  onEdit: VoidFunction;
  /** Gets the trigger so the confirmation can hand the focus back to it when it closes. */
  onDelete: (trigger: HTMLButtonElement | null) => void;
  /** A delete is in flight: the menu cannot be opened meanwhile. */
  isDeleting: boolean;
}

/**
 * The "…" menu of the active group (RES-1232): Edit group / Delete group. Deleting lives here, behind
 * a deliberate second tap, rather than as an × on the chip next to the control that selects it.
 *
 * Plain elements rather than a menu library: two items, and the sheet it sits in is itself built of
 * plain elements. Escape closes the menu alone (captured before the sheet's own Escape handler sees
 * it), and so does a press anywhere outside it.
 */
export const CoworkerGroupActionsMenu = ({ onEdit, onDelete, isDeleting }: CoworkerGroupMenuProps): JSX.Element => {
  const intl = useIntl();
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => setIsOpen(value => !value), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const getItems = () => Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

    // A menu takes the focus when it opens, so the keyboard lands on its first item.
    getItems()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Capture phase on window: the sheet's document-level handler never sees this Escape.
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();

        return;
      }

      if (event.key === 'Tab') {
        // Tab leaves the menu — it closes and the browser moves the focus on.
        setIsOpen(false);

        return;
      }

      const items = getItems();
      const index = items.indexOf(document.activeElement as HTMLElement);
      const targets: Record<string, number | undefined> = {
        ArrowDown: (index + 1) % items.length,
        ArrowUp: (index - 1 + items.length) % items.length,
        Home: 0,
        End: items.length - 1,
      };
      const target = targets[event.key];

      if (target !== undefined && items.length) {
        event.preventDefault();
        event.stopPropagation();
        items[target].focus();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [isOpen]);

  const handleEdit = useCallback(() => {
    close();
    onEdit();
  }, [close, onEdit]);
  const handleDelete = useCallback(() => {
    close();
    onDelete(triggerRef.current);
  }, [close, onDelete]);

  return (
    <span ref={containerRef}>
      <CoworkerGroupMenuTrigger
        ref={triggerRef}
        type="button"
        data-testid="coworker-group-menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={intl.formatMessage({ id: 'coworkerGroupActions' })}
        disabled={isDeleting}
        onClick={toggle}
      >
        <MoreIcon />
      </CoworkerGroupMenuTrigger>
      {isOpen && (
        <CoworkerGroupMenu role="menu" data-testid="coworker-group-menu-list">
          <CoworkerGroupMenuItem type="button" role="menuitem" data-testid="coworker-group-edit" onClick={handleEdit}>
            <PencilIcon />
            {intl.formatMessage({ id: 'editCoworkerGroup' })}
          </CoworkerGroupMenuItem>
          <CoworkerGroupMenuDivider />
          <CoworkerGroupMenuItem
            type="button"
            role="menuitem"
            isDestructive
            data-testid="coworker-group-delete"
            onClick={handleDelete}
          >
            <TrashIcon />
            {intl.formatMessage({ id: 'deleteCoworkerGroup' })}
          </CoworkerGroupMenuItem>
        </CoworkerGroupMenu>
      )}
    </span>
  );
};
